<template>
  <div class="relative w-full">
    <textarea
      :data-cy="`textarea-${name}`"
      v-model="inputValue"
      ref="textareaRef"
      class="w-full rounded-sm border-gray-200 text-xs text-gray-600 placeholder-gray-400 placeholder:text-sm focus:border-primary"
      :class="{ 'pb-9': characterLimit, 'resize-none': noresize }"
      v-bind="$attrs"
      :name="name"
      :placeholder="placeholder"
      :rows="rows"
      :readonly="readonly"
      :maxlength="characterLimit || undefined"
      :disabled="disabled"
      @input="handleInput"
      @focus="focused = true"
      @blur="onBlur"
    />
    <label
      v-show="!isNil(inputValue) && String(inputValue).length && showLabel"
      for="input"
      class="absolute top-0 left-0 z-1 -mt-3 ml-2-5 bg-white px-1 text-xs leading-6"
      :class="
        errorMessage
          ? 'text-red-600'
          : focused
            ? 'text-blue-500'
            : 'text-gray-400'
      "
    >
      {{ placeholder }}
    </label>
    <div
      v-if="characterLimit && inputValue"
      class="absolute right-5 bottom-1.5 left-px flex flex-row items-center bg-white py-1 pl-3"
    >
      <circle-progress
        :size="20"
        :border-width="3"
        :border-bg-width="3"
        :percent="percentageUsed"
        fill-color="#3769D6"
      />
      <div class="ml-2 text-xxs font-semibold">
        {{ inputValue.length }} / {{ characterLimit }}
      </div>
    </div>
  </div>
  <div
    v-show="errorMessage"
    class="mb-4 pl-2 text-left text-xxs text-red-700 sm:text-xs"
  >
    {{ errorMessage }}
  </div>
</template>
<script setup lang="ts">
import { until } from "@vueuse/core";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import CircleProgress from "vue3-circle-progress";

import isNil from "lodash/isNil";
import { useField } from "vee-validate";

const emit = defineEmits<{ keyReleased: [value: string] }>();

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  value: {
    type: [String],
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  rows: {
    type: [Number, String],
    default: 4,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
  // this prop is left unused - added to remove warning in iteration
  modelValue: {
    type: String,
    default: () => "",
  },
  characterLimit: {
    type: Number,
    default: 0,
  },
  noresize: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
  },
  immediate: {
    type: Boolean,
    default: false,
  },
});

const {
  value: inputValue,
  errorMessage,
  meta,
  handleBlur,
  handleChange,
} = useField(props.name, undefined, {
  initialValue: props.value,
});

const focused = ref(false);

const textareaRef = useTemplateRef("textareaRef");

const percentageUsed = computed(() =>
  inputValue.value?.length
    ? (inputValue.value.length / props.characterLimit) * 100
    : 0
);

const onBlur = (e: Event) => {
  handleBlur(e);
  focused.value = false;
};

const handleInput = (e: Event) => {
  emit("keyReleased", (e.target as HTMLInputElement).value);
  handleChange(e);
};

watch(
  () => props.value,
  async (newVal) => {
    await nextTick();
    await until(() => meta.pending).toBe(false);
    handleChange(newVal);
    inputValue.value = newVal;
  },
  { immediate: props.immediate }
);

defineExpose({
  textareaRef,
});
</script>
